import { useEffect, useRef, useState } from "react";

/**
 * The loader's choreography, in one place. These mirror the timings in
 * `components/cxm/cxm-route-loader.scss` and must move with them.
 *
 * `ASSEMBLE` is when the logo actually finishes: the sweep spans 0.42s and the
 * last shape animates for 0.36s, so the mark completes at 0.78s. This was 700
 * before, which is arithmetic that ignored the shape duration — the pane began
 * leaving while the final letter was barely filled, and the exit showed a
 * half-drawn logo over half-cleared glass.
 *
 * `REST` is a beat on the completed mark and `EXIT` is the glass clearing, so
 * the finished logo is on screen for the rest plus the whole dissolve.
 */
const LOADER_ASSEMBLE_MS = 780;
const LOADER_REST_MS = 120;
const LOADER_EXIT_MS = 300;

export type CxmLoaderPhase = "hold" | "leaving" | "done";

/**
 * Sequences the pane against the logo rather than against the network: hold
 * while the mark assembles, then clear the glass, and only then is the form
 * allowed on screen. Mounting the form earlier would also resize the iframe
 * mid-animation, since the widget reports its height to the host page.
 *
 * Lives here rather than in either page because both the payout and the
 * deposit page need the identical sequence, and a copy would drift from the
 * stylesheet independently.
 */
export const useCxmLoaderSequence = (
  loading: boolean
): { phase: CxmLoaderPhase; revealed: boolean } => {
  const [phase, setPhase] = useState<CxmLoaderPhase>(loading ? "hold" : "done");
  const shownAt = useRef<number>(Date.now());

  useEffect(() => {
    if (loading) {
      setPhase("hold");
      shownAt.current = Date.now();
      return undefined;
    }
    if (phase !== "hold") return undefined;

    const elapsed = Date.now() - shownAt.current;
    const remaining = Math.max(0, LOADER_ASSEMBLE_MS + LOADER_REST_MS - elapsed);
    const toLeaving = window.setTimeout(() => setPhase("leaving"), remaining);
    const toDone = window.setTimeout(() => setPhase("done"), remaining + LOADER_EXIT_MS);

    return () => {
      window.clearTimeout(toLeaving);
      window.clearTimeout(toDone);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return { phase, revealed: phase !== "hold" };
};
